import React, { FC, PropsWithChildren, isValidElement, Children, useState, cloneElement, useEffect } from 'react';
import { View } from '@tarojs/components';
import TabPane from './TabPane';

interface ITabItem {
  key: string;
  title: string;
}

interface ITabsProps {
  className?: string;
  defaultActiveKey?: string;
  activeKey?: string;
  animated?: boolean;
  onChange?: (key: string) => void;
}

const parseTabList = (children: React.ReactNode) => {
  return (
    Children.map(children, (node) => {
      if (isValidElement(node)) {
        const { key, props: { tab = '' } = {} } = node;
        return {
          key: key !== undefined ? String(key) : undefined,
          title: tab,
        };
      }
      return null;
    }) || []
  ).filter((tab) => tab) as ITabItem[];
};

const Tabs: FC<PropsWithChildren<ITabsProps>> & { TabPane: typeof TabPane } = ({
  className = '',
  defaultActiveKey,
  activeKey: propsActiveKey,
  children,
  onChange,
  animated = true,
}) => {
  const tabs = parseTabList(children);
  const [{ key: firstTabKey = undefined } = {}] = tabs;
  const [activeKey, setActiveKey] = useState(firstTabKey);
  // const currentIndex = tabs.findIndex(({ key }) => key === activeKey);

  useEffect(() => {
    setActiveKey(propsActiveKey || defaultActiveKey || firstTabKey);
  }, [propsActiveKey, defaultActiveKey, firstTabKey]);

  return (
    <View className={`ace-tabs ${className}`}>
      <View className="ace-tabs-header">
        {tabs.map(({ key, title }) => (
          <View
            key={key}
            onClick={() => {
              setActiveKey(key);
              if (onChange) {
                onChange(key);
              }
            }}
            className={`ace-tabs-header-item ${key === activeKey ? 'ace-tabs-header-item__active' : ''}`}
          >
            {title}
          </View>
        ))}
      </View>
      <View className={`ace-tabs-body ${animated ? 'ace-tabs-body__animated' : ''}`}>
        {Children.map(children, (node) => (isValidElement(node) ? cloneElement(node, { id: node.key, activeKey }) : node))}
      </View>
    </View>
  );
};

Tabs.TabPane = TabPane;

export default Tabs;
